import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { checkEnabledPointsModule } from 'utils/helpers/featureFlags';

import type { AttendanceRule, EscalationRule, MissedShiftRule } from '../settings/types';
import { mockRulesData } from './mockRulesData';

export const POINTS_RULE_TYPES = {
  ATTENDANCE_BASED_RULES: 'attendanceBasedRules',
  THRESHOLD_AND_ESCALATIONS: 'thresholdAndEscalations',
};

export const POINTS_RULE_SUBTYPES = {
  LATE_ARRIVALS: 'lateArrivals',
  EARLY_DEPARTURES: 'earlyDepartures',
  MISSED_SHIFTS: 'missedShifts',
  ESCALATION_RULES: 'escalationRules',
};

export interface PointsSettingsState {
  // Points expiry
  rollingExpiryDays: number;
  rules: {
    attendanceBasedRules: {
      lateArrivalRules: AttendanceRule[];
      earlyDepartureRules: AttendanceRule[];
      missedShiftRules: MissedShiftRule[];
    };
    thresholdAndEscalations: {
      thresholdRules: EscalationRule[];
    };
  };
  isEnableManagerApproval: boolean;
  originalData: {
    rollingExpiryDays: number;
    rules: unknown;
  };
  isEnableRequiredDataValidation: boolean;
}
interface RootState {
  pointsSettings: PointsSettingsState;
}

const filterBESupportedRules = (rules) => {
  return {
    attendanceBasedRules: {
      lateArrivals: rules?.attendanceBasedRules?.lateArrivals ?? [],
      earlyDepartures: rules?.attendanceBasedRules?.earlyDepartures ?? [],
      missedShifts: rules?.attendanceBasedRules?.missedShifts ?? [],
    },
  };
};

const checkIsMissingRequiredData = ({ rule, ruleSubType }) => {
  switch (ruleSubType) {
    case POINTS_RULE_SUBTYPES.LATE_ARRIVALS:
    case POINTS_RULE_SUBTYPES.EARLY_DEPARTURES: {
      const { thresholdMaxMinutes, thresholdMinMinutes, appliesToDays, pointsAwarded } = rule;

      // "Above" range case, if it's not null, then it's an applicable field
      // so we check for undefined (meaning empty)
      // just early return failure case.
      if (thresholdMaxMinutes !== null) {
        if (thresholdMaxMinutes === undefined) {
          return true;
        }
      }
      return !(thresholdMinMinutes && appliesToDays && pointsAwarded);
    }

    case POINTS_RULE_SUBTYPES.MISSED_SHIFTS: {
      const { infractionType, pointsAwarded } = rule;
      return !(infractionType && pointsAwarded);
    }

    case POINTS_RULE_SUBTYPES.ESCALATION_RULES: {
      const { stakeholdersToNotify, escalationType, reviewCycle, disciplinaryNoteType, severity, pointsThreshold } =
        rule;
      if (escalationType === 'NO_ACTION') {
        return !(stakeholdersToNotify.length >= 2 && escalationType && pointsThreshold);
      }
      if (escalationType === 'DISCIPLINARY_NOTE') {
        return !(stakeholdersToNotify.length && escalationType && disciplinaryNoteType && severity && pointsThreshold);
      }
      if (escalationType === 'PIP_REVIEW') {
        return !(stakeholdersToNotify.length && escalationType && reviewCycle && pointsThreshold);
      }
      return false;
    }

    default:
      return false;
  }
};

const checkForMissingRequiredData = ({ ruleSubType, rules }) => {
  // logic to check for missing required data based on ruleSubType and rules

  // reset all isMissingRequiredData flags
  const rulesNew = rules.map((rule) => ({ ...rule, isMissingRequiredData: false }));

  for (let i = 0; i < rulesNew.length; i++) {
    if (checkIsMissingRequiredData({ rule: rulesNew[i], ruleSubType })) {
      rulesNew[i].isMissingRequiredData = true;
    }
  }
  return rulesNew;
};

const checkIsDuplicateRule = ({ rule1, rule2, ruleSubType }) => {
  switch (ruleSubType) {
    case POINTS_RULE_SUBTYPES.LATE_ARRIVALS:
    case POINTS_RULE_SUBTYPES.EARLY_DEPARTURES: {
      return (
        rule1.thresholdMinMinutes === rule2.thresholdMinMinutes &&
        rule1.thresholdMaxMinutes === rule2.thresholdMaxMinutes &&
        rule1.shiftFilterMinHours === rule2.shiftFilterMinHours &&
        rule1.shiftFilterMaxHours === rule2.shiftFilterMaxHours &&
        rule1.appliesToDays === rule2.appliesToDays
      );
    }

    case POINTS_RULE_SUBTYPES.MISSED_SHIFTS: {
      return rule1.infractionType === rule2.infractionType;
    }

    case POINTS_RULE_SUBTYPES.ESCALATION_RULES: {
      return (
        rule1.stakeholdersToNotify.length === rule2.stakeholdersToNotify.length &&
        rule1.escalationType === rule2.escalationType &&
        rule1.reviewCycle === rule2.reviewCycle &&
        rule1.disciplinaryNoteType === rule2.disciplinaryNoteType &&
        rule1.severity === rule2.severity
      );
    }

    default: {
      return false;
    }
  }
};

const checkForDuplicateRules = ({ ruleSubType, rules }) => {
  // logic to check for duplicate rules based on ruleSubType and rules

  // reset all isDuplicate flags
  const rulesNew = rules.map((rule) => ({ ...rule, isDuplicate: false }));

  for (let i = 0; i < rulesNew.length; i++) {
    for (let j = i + 1; j < rulesNew.length; j++) {
      if (checkIsDuplicateRule({ rule1: rulesNew[i], rule2: rulesNew[j], ruleSubType })) {
        rulesNew[i].isDuplicate = true;
        rulesNew[j].isDuplicate = true;
      }
    }
  }
  return rulesNew;
};

const initialState: PointsSettingsState = {
  rollingExpiryDays: null,
  rules: {},
  isEnableManagerApproval: true,
  // this is used to store the original data fetched from API for comparison
  // the comparison is used to show unsaved changes prompt
  originalData: {
    rollingExpiryDays: null,
    rules: {},
  },
  isEnableRequiredDataValidation: false,
};

const pointsSettingsSlice = createSlice({
  name: 'pointsSettings',
  initialState,
  reducers: {
    // Points expiry actions
    setRollingExpirationPeriodDays: (state, action: PayloadAction<number>) => {
      state.rollingExpiryDays = action.payload;
    },

    // used in case of mock demo data
    setPointsMockDemoState: (state) => {
      const newOriginalData = { rollingExpiryDays: mockRulesData.rollingExpiryDays, rules: mockRulesData.rules };
      return { ...state, ...mockRulesData, originalData: newOriginalData };
    },

    // used in case of api fetched data
    setPointsInitialData: (state, action) => {
      const newOriginalData = {
        rollingExpiryDays: action.payload.rollingExpiryDays,
        rules: filterBESupportedRules(action.payload.rules),
      };
      return { ...state, ...action.payload, originalData: newOriginalData };
    },

    setIsEnableRequiredDataValidation: (state) => {
      state.isEnableRequiredDataValidation = true;
    },

    addPointsRule: (
      state,
      action: PayloadAction<{
        ruleType: keyof typeof POINTS_RULE_TYPES;
        ruleSubType: keyof typeof POINTS_RULE_SUBTYPES;
        rule: MissedShiftRule | AttendanceRule | EscalationRule;
      }>
    ) => {
      const { ruleType, ruleSubType, rule } = action.payload;
      const newRulesList = [...state.rules[ruleType][ruleSubType], rule];
      const newRulesWithDuplicatesCheck = checkForDuplicateRules({ ruleSubType, rules: newRulesList });
      const newRulesWithMissingDataCheck = checkForMissingRequiredData({
        ruleSubType,
        rules: newRulesWithDuplicatesCheck,
      });
      state.rules[ruleType][ruleSubType] = newRulesWithMissingDataCheck;
    },

    updatePointsRule: (
      state,
      action: PayloadAction<{
        ruleType: keyof typeof POINTS_RULE_TYPES;
        ruleSubType: keyof typeof POINTS_RULE_SUBTYPES;
        rule: MissedShiftRule | AttendanceRule | EscalationRule;
        ruleIndex: number;
      }>
    ) => {
      const { ruleType, ruleSubType, rule, ruleIndex } = action.payload;
      const rulesArray = state.rules[ruleType][ruleSubType];
      const newRuleData = { ...rulesArray[ruleIndex], ...rule };
      const newRulesList = [...rulesArray];

      newRulesList[ruleIndex] = newRuleData;
      const newRulesWithDuplicatesCheck = checkForDuplicateRules({ ruleSubType, rules: newRulesList });
      const newRulesWithMissingDataCheck = checkForMissingRequiredData({
        ruleSubType,
        rules: newRulesWithDuplicatesCheck,
      });
      state.rules[ruleType][ruleSubType] = newRulesWithMissingDataCheck;
    },

    removePointsRule: (
      state,
      action: PayloadAction<{
        ruleType: keyof typeof POINTS_RULE_TYPES;
        ruleSubType: keyof typeof POINTS_RULE_SUBTYPES;
        ruleId: string;
      }>
    ) => {
      const { ruleType, ruleSubType, ruleId } = action.payload;

      let newRulesList = state.rules[ruleType][ruleSubType].filter((rule) => rule.id !== ruleId);
      // update displayOrder of remaining rules
      newRulesList = newRulesList.map((rule, index) => ({
        ...rule,
        displayOrder: index + 1,
      }));

      const newRulesWithDuplicatesCheck = checkForDuplicateRules({ ruleSubType, rules: newRulesList });
      const newRulesWithMissingDataCheck = checkForMissingRequiredData({
        ruleSubType,
        rules: newRulesWithDuplicatesCheck,
      });
      state.rules[ruleType][ruleSubType] = newRulesWithMissingDataCheck;
    },

    updateIsEnableManagerApproval: (state, action: PayloadAction<boolean>) => {
      state.isEnableManagerApproval = action.payload;
    },

    resetPointsSettingsState: () => initialState,
  },
});

export const {
  setRollingExpirationPeriodDays,
  addPointsRule,
  updatePointsRule,
  removePointsRule,
  updateIsEnableManagerApproval,
  setPointsMockDemoState,
  setPointsInitialData,
  resetPointsSettingsState,
  setIsEnableRequiredDataValidation,
} = pointsSettingsSlice.actions;

// Selectors

export const useIsEnableManagerApproval = () =>
  useSelector((state: RootState) => state.pointsSettings.isEnableManagerApproval);

export const useOriginalPointsSettingsData = () => useSelector((state: RootState) => state.pointsSettings.originalData);

export const useRollingExpirationPeriodDays = () =>
  useSelector((state: RootState) => state.pointsSettings.rollingExpiryDays);

export const useIsEnableRequiredDataValidation = () =>
  useSelector((state: RootState) => state.pointsSettings.isEnableRequiredDataValidation);

export const useViolationPointsRules = () =>
  useSelector((state: RootState) => {
    // only return BE supported rules if api integrated flag is ON
    if (checkEnabledPointsModule()) {
      return filterBESupportedRules(state.pointsSettings.rules);
    }
    return state.pointsSettings.rules;
  });

export const useRuleDetailsList = (
  ruleType: keyof typeof POINTS_RULE_TYPES,
  ruleSubType: keyof typeof POINTS_RULE_SUBTYPES
) => useSelector((state: RootState) => state.pointsSettings.rules?.[ruleType]?.[ruleSubType] ?? []);

export const usePolicyDetails = () => useSelector((state: RootState) => state.pointsSettings);

export default pointsSettingsSlice.reducer;
